import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function SponsorDialog({ open, onOpenChange, eventId, onSponsored }) {
  const [loading, setLoading] = useState(null);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    loadRazorpayScript();
    api.get("/sponsor/tiers").then((r) => setTiers(r.data.tiers || [])).catch(() => setTiers([]));
  }, []);

  const start = async (tierKey) => {
    setLoading(tierKey);
    try {
      const { data: order } = await api.post("/sponsor/order", { event_id: eventId, tier: tierKey });

      if (order.mock_activated) {
        toast.success("Boost activated in demo mode. Paste real Razorpay keys in backend/.env to charge users.");
        onOpenChange(false);
        onSponsored?.();
        setLoading(null);
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load Razorpay checkout. Please try again.");
        setLoading(null);
        return;
      }

      const t = tiers.find((x) => x.key === tierKey);
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Fanpit.Club",
        description: t?.name || "Boost",
        theme: { color: "#FA003F" },
        handler: async (res) => {
          try {
            await api.post("/sponsor/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Boost activated 🎉");
            onOpenChange(false);
            onSponsored?.();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(null) },
      });
      rz.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sponsor-dialog" className="max-w-2xl rounded-3xl bg-white p-8 border border-black/10">
        <DialogHeader>
          <div className="h-12 w-12 rounded-2xl bg-[#FA003F]/10 text-[#FA003F] grid place-items-center mb-2">
            <Sparkles className="h-5 w-5" />
          </div>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">Boost this event</DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            Pin your listing to the hero and get a sponsored badge — instant visibility for locals in the city.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {tiers.length === 0 && (
            <div className="col-span-2 text-center py-8 text-gray-500">No boost packages available.</div>
          )}
          {tiers.map((t) => (
            <div
              key={t.key}
              data-testid={`sponsor-tier-${t.key}`}
              className={`relative rounded-2xl p-6 border-2 transition-all ${
                t.highlight ? "border-[#FA003F] bg-[#FA003F]/5" : "border-black/10 bg-white hover:border-[#FA003F]/40"
              }`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-6 bg-[#FA003F] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                  Best value
                </span>
              )}
              <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{t.tagline || "Sponsor tier"}</div>
              <div className="font-display text-2xl font-black mt-1">{t.name}</div>
              <div className="font-display text-4xl font-black mt-3 text-[#FA003F]">₹{Number(t.amount).toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-0.5">for {t.days} days</div>
              <ul className="mt-4 space-y-2">
                {(t.perks || []).map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`sponsor-buy-${t.key}`}
                disabled={loading !== null}
                onClick={() => start(t.key)}
                className={`w-full mt-6 h-11 rounded-full font-bold ${
                  t.highlight ? "bg-[#FA003F] hover:bg-[#C40031]" : "bg-[#1A1A1A] hover:bg-[#FA003F]"
                }`}
              >
                {loading === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : `Boost with ${t.name}`}
              </Button>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500 text-center mt-2">
          Secure Razorpay checkout · UPI · Cards · Netbanking · Wallets
        </div>
      </DialogContent>
    </Dialog>
  );
}
